import React, { FC, useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Avatar from '@material-ui/core/Avatar';
import IconButton from '@material-ui/core/IconButton';
import ListItem from '@material-ui/core/ListItem';
import ListItemAvatar from '@material-ui/core/ListItemAvatar';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
import ListItemText from '@material-ui/core/ListItemText';
import Tooltip from '@material-ui/core/Tooltip';
import PencilIcon from 'mdi-react/PencilIcon';
import DeleteIcon from 'mdi-react/DeleteIcon';
import ArrowUpIcon from 'mdi-react/ArrowUpIcon';
import ArrowDownIcon from 'mdi-react/ArrowDownIcon';
import PlusIcon from 'mdi-react/PlusIcon';
import { Color, borderRadius } from '../Theme';
import InfoIcon from 'mdi-react/InfoOutlineIcon';
import styled from 'styled-components';
import NotHiddenIcon from 'mdi-react/EyeIcon';
import HiddenIcon from 'mdi-react/EyeOffIcon';

const ImportedLocationInfo = styled.div`
  color: #fe8522;
  display: flex;
  > * {
    vertical-align: top;
    margin-right: 5px;
  }
`;

/**
 * List item with move, edit & remove buttons built in
 */
export const EditListItem: FC<{
  id: number;
  title: string | JSX.Element;
  subtitle: string | JSX.Element | null;
  icon: JSX.Element;
  disableEditing?: boolean;
  isHidden?: boolean;
  onToggleIsHidden?: ((id: number) => void) | null;
  onMove?: (id: number, direction: number) => void;
  onRemove?: (id: number) => void;
  onEdit?: (id: number) => void;
}> = ({ id, title, subtitle = null, icon, disableEditing, isHidden, onToggleIsHidden, onMove, onRemove, onEdit }) => {
  const intl = useIntl();

  const hideShowLabel = !!isHidden
    ? intl.formatMessage({ id: 'company.edit.show', defaultMessage: 'Näytä' })
    : intl.formatMessage({ id: 'company.edit.hide', defaultMessage: 'Piilota' });
  const moveUpLabel = intl.formatMessage({ id: 'company.edit.moveUpButton', defaultMessage: 'siirrä ylemmäs' });
  const moveDownLabel = intl.formatMessage({ id: 'company.edit.moveDownButton', defaultMessage: 'siirrä alemmas' });
  const editLabel = intl.formatMessage({ id: 'company.edit.editButton', defaultMessage: 'muokkaa' });
  const removeLabel = intl.formatMessage({ id: 'company.edit.removeButton', defaultMessage: 'poista' });

  const handleToggleIsHidden = useCallback(() => onToggleIsHidden && onToggleIsHidden(id), [id, onToggleIsHidden]);
  const handleMoveUp = useCallback(() => onMove && onMove(id, 1), [id, onMove]);
  const handleMoveDown = useCallback(() => onMove && onMove(id, -1), [id, onMove]);
  const handleEdit = useCallback(() => onEdit && onEdit(id), [id, onEdit]);
  const handleRemove = useCallback(() => !onToggleIsHidden && onRemove(id), [id, onRemove]);

  return (
    <ListItem disableGutters>
      <ListItemAvatar>
        <Avatar>{icon}</Avatar>
      </ListItemAvatar>
      <ListItemText
        primary={title}
        secondary={
          <>
            <div>{subtitle}</div>
            {!!onToggleIsHidden && (
              <ImportedLocationInfo>
                <InfoIcon />
                <FormattedMessage id="company.edit.importedLocation" defaultMessage="Haettu rekisteristä" />
              </ImportedLocationInfo>
            )}
          </>
        }
      />
      <ListItemSecondaryAction>
        {onMove && (
          <IconButton aria-label={moveUpLabel} onClick={handleMoveUp}>
            <ArrowUpIcon />
          </IconButton>
        )}
        {onMove && (
          <IconButton aria-label={moveDownLabel} onClick={handleMoveDown}>
            <ArrowDownIcon />
          </IconButton>
        )}
        {!disableEditing && onEdit && (
          <Tooltip title="Muokkaa" placement="left" arrow>
            <IconButton aria-label={editLabel} onClick={handleEdit}>
              <PencilIcon />
            </IconButton>
          </Tooltip>
        )}

        {onToggleIsHidden && (
          <Tooltip title={hideShowLabel} placement="top" arrow>
            <IconButton aria-label={hideShowLabel} onClick={handleToggleIsHidden}>
              {!isHidden ? <NotHiddenIcon /> : <HiddenIcon style={{ color: '#ccc' }} />}
            </IconButton>
          </Tooltip>
        )}
        {onRemove && (
          <Tooltip title="Poista" placement="right" arrow>
            <IconButton edge="end" aria-label={removeLabel} onClick={handleRemove}>
              <DeleteIcon color={Color.red} />
            </IconButton>
          </Tooltip>
        )}
      </ListItemSecondaryAction>
    </ListItem>
  );
};

export const EditListCreateButton: FC<{ onClick: () => void }> = ({ onClick }) => (
  <ListItem disableGutters button style={{ borderRadius }} onClick={onClick}>
    <ListItemAvatar>
      <Avatar style={{ background: 'transparent' }}>
        <PlusIcon color={Color.primary} />
      </Avatar>
    </ListItemAvatar>
    <ListItemText primary={<FormattedMessage id="company.location.createButton" defaultMessage="Luo uusi" />} />
  </ListItem>
);
